import { Field, FieldProps, Form, Formik, FormikValues } from 'formik';
import { FC, useState } from 'react';
import { Need, ValueRanges } from 'rlv-common';
import AemFragment from '../../../../components/AemFragment/AemFragment';
import Footer from '../../../../components/Footer';
import { Headline } from '../../../../components/Headline/Headline';
import InfoText from '../../../../components/InfoText/infoText';
import RadioGroup from '../../../../components/RadioGroup/RadioGroup';
import { ScrollToError } from '../../../../components/ScrollToError';
import { getServiceConfiguration } from '../../../../configuration/serviceConfiguration';
import { getIsTiedAgent } from '../../../../helpers/modeConfig';
import FallIcon from '../../../../images/SumInsuredFallingIcon.svg';
import ConstantIcon from '../../../../images/SumIsuredLinearIcon.svg';
import { NavigationAction } from '../../../../routing/StateMachineTypes';
import { AemFragmentCatalog } from '../../../../types/AemFragmentCatalog';
import { SharedSubmitCallBack } from '../../../../types/callbacks/sharedSubmitCallBack';
import { createNeedPageSchema } from '../../../../validation/NeedPage';
import './NeedSelection.css';

interface NeedSelectionProps extends SharedSubmitCallBack<Need> {
  need?: Need;
  valueRanges: ValueRanges;
  onSubmit: (needSelection: Need, navigationRequest: NavigationAction) => void;
  onSelect: (needSelection: Need) => void;
}

const NeedSelection: FC<NeedSelectionProps> = ({ need, valueRanges, onSubmit, onSelect }) => {
  const [needSelection, setNeedSelection] = useState(need);
  const [navigationRequest, setNavigationRequest] = useState(NavigationAction.NEXT);

  const headline = 'Wählen Sie Ihre Absicherungsform aus';
  const footerSectionText = (
    <span>
      <b>Ihre Lösung ist nicht dabei?</b> ERGO bietet flexible Lösungen für unterschiedliche
      Darlehen und Lebenssituationen an. Bitte wenden Sie sich an den{' '}
      <b>gebührenfreien telefonischen Beratungsservice</b> (7 bis 24 Uhr) oder Ihren{' '}
      <a target="_blank" href="https://www.ergo.de/de/Service/Vermittlersuche">
        ERGO Berater vor Ort.
      </a>
    </span>
  );

  const footerInfoSection = (
    <InfoText textBeforeLink={getIsTiedAgent() ? '' : footerSectionText}></InfoText>
  );

  const RADIO_OPTIONS = [
    {
      title: 'Familien- / Partnerabsicherung',
      value: Need.KONSTANT,
      description: (
        <span>
          Im Todesfall ist Ihre Familie oder Ihr Partner mit einer{' '}
          <b>gleichbleibenden Versicherungssumme</b> abgesichert.
        </span>
      ),
      icon: (
        <div className="radio_groups--icon">
          <img src={`${getServiceConfiguration().iconsPath}${ConstantIcon}`} alt="constant" />
        </div>
      ),
    },
    {
      title: 'Kredit- / Darlehensabsicherung',
      value: Need.DECREASING,
      description: (
        <span>
          Im Todesfall erhalten Ihre Hinterbliebenen eine Summe zur Ablösung von Darlehen und/oder
          Krediten. Die <b>Versicherungssumme sinkt jährlich</b> analog zum Kredit (linear fallend).
        </span>
      ),
      icon: (
        <div className="radio_groups--icon">
          <img src={`${getServiceConfiguration().iconsPath}${FallIcon}`} alt="falling" />
        </div>
      ),
    },
  ];

  const fragmentName =
    needSelection?.toString() === 'LINEAR_FALLEND'
      ? AemFragmentCatalog.STEP1VA2
      : AemFragmentCatalog.STEP1VA1;

  return (
    <Formik
      initialValues={{ need, valueRanges }}
      onSubmit={() => onSubmit(needSelection!, navigationRequest)}
      validationSchema={createNeedPageSchema(valueRanges)}
    >
      {form => (
        <Form className="need-selection__container" data-testid="need-selection">
          <Headline text={headline} />
          <Field
            name="need"
            render={({ field }: FieldProps<FormikValues>) => {
              const { setFieldValue } = form;
              return (
                <RadioGroup
                  options={RADIO_OPTIONS}
                  selectedValue={field.value || needSelection}
                  name="need"
                  onChangeSelection={(_, value) => {
                    const selectedNeed = value as Need;
                    setFieldValue(field.name, value);
                    setNeedSelection(selectedNeed);
                    onSelect(selectedNeed);
                  }}
                />
              );
            }}
          />
          {form.errors.need && (
            <div style={{ color: 'red', clear: 'both', float: 'left' }}>
              <p>{form.errors.need}</p>
            </div>
          )}
          <AemFragment name={fragmentName} useVA={true} />
          <Footer
            showNext
            showPrevious
            labelNext="weiter"
            handleAction={(navigationAction: NavigationAction) => {
              setNavigationRequest(navigationAction);
              form.submitForm();
            }}
            infoSection={footerInfoSection}
          />
          <ScrollToError formik={form} />
        </Form>
      )}
    </Formik>
  );
};

export default NeedSelection;
